import { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import ReactQuill from "react-quill";
import axios from "axios";

import { useNewCampaignForm } from "./hooks/useNewCampaignForm";
import FilterRecipientsModal from "../components/FilterRecipientsModal";
import InputField from "../components/InputField";

import "react-quill/dist/quill.snow.css";
import base64ToFile from "../utils/base64ToFile";

export interface IEmailImage {
  fileName: string;
  placeholder: string | null;
  url: string | null;
  isUsed: boolean;
  base64String: string | null;
  s3_key?: string | null;
}

const NewCampaign = () => {
  const { id } = useParams<{ id?: string }>();
  const isEditing = Boolean(id);
  const quillRef = useRef<ReactQuill>(null);
  const [quillInstance, setQuillInstance] = useState<any>(null);
  const [quillKey, setQuillKey] = useState(0);
  const [emailImages, setEmailImages] = useState<IEmailImage[]>([]);

  const {
    tags,
    campaignName,
    setCampaignName,
    subject,
    setSubject,
    emailContent,
    setEmailContent,
    sendDate,
    setSendDate,
    isFilterModalOpen,
    setIsFilterModalOpen,
    includeZipCodes,
    setIncludeZipCodes,
    excludeZipCodes,
    setExcludeZipCodes,
    includeCities,
    setIncludeCities,
    excludeCities,
    setExcludeCities,
    includeStates,
    setIncludeStates,
    excludeStates,
    setExcludeStates,
    includeTags,
    setIncludeTags,
    excludeTags,
    setExcludeTags,
    touched,
    setTouched,
    errors,
    isFormValid,
    loading,
    handleSubmit,
  } = useNewCampaignForm();

  useEffect(() => {
    if (isEditing) {
      axios
        .get(`${process.env.REACT_APP_SERVER_URL}/api/v1/company/outreach/email-campaign/${id}`)
        .then((response) => {
          const campaign = response.data;



          let localDateTime = "";
          if (campaign.send_date) {
            const utcDate = new Date(campaign.send_date);
            const localOffset = utcDate.getTimezoneOffset() * 60000;
            const localTime = new Date(utcDate.getTime() - localOffset);
            localDateTime = localTime.toISOString().slice(0, 16);
          }

          setCampaignName(campaign.name);
          setSubject(campaign.subject);
          setSendDate(localDateTime);
          setIncludeZipCodes(campaign.include_zip_codes || []);
          setExcludeZipCodes(campaign.exclude_zip_codes || []);
          setIncludeCities(campaign.include_cities || []);
          setExcludeCities(campaign.exclude_cities || []);
          setIncludeStates(campaign.include_states || []);
          setExcludeStates(campaign.exclude_states || []);
          setIncludeTags(campaign.include_tags || []);
          setExcludeTags(campaign.exclude_tags || []);

          let emailBody = campaign.body;

          // Find all <img src="..."> placeholders
          const imgPlaceholderRegex = /<img\s+src="([^"]+)"/g;
          emailBody = emailBody.replace(imgPlaceholderRegex, (match: string, src: string): string => {
            // Check if this src matches a placeholder
            const matchedImage = campaign.images.find((img: IEmailImage) => img.placeholder === src);
            return matchedImage ? match.replace(src, matchedImage.url || "") : match;
          });



          setEmailContent(emailBody);

        })
        .catch((error) => console.error("Error fetching campaign:", error));
    }
  }, [id, isEditing]);

  const handleEmailContentChange = useCallback(async (value: string) => {


    if (!quillInstance) return;

    const editor = quillInstance;
    const range = editor.getSelection();

    // Extract image sources
    const imageRegex = /<img\s+src="([^"]+)"/g;
    const matches = [...value.matchAll(imageRegex)];
    const detectedImages = new Set(matches.map(match => match[1]));



    let updatedContent = value;
    let updatedImages = [...emailImages];

    // Process detected images
    for (const imgSrc of detectedImages) {
      // ✅ Check if the image is already uploaded
      const alreadyUploaded = updatedImages.some(img => img.base64String === imgSrc || img.url === imgSrc);
      if (alreadyUploaded) {
        if (imgSrc.startsWith("data:image/")) {
          // Replace base64 string with url if available


          updatedContent = updatedContent.replace(imgSrc, updatedImages.find(img => img.base64String === imgSrc)?.url || "");
        }

        continue;
      }

      if (imgSrc.startsWith("data:image/")) {

        const file = base64ToFile(imgSrc, `image-${updatedImages.length + 1}`);
        if (!file) continue;

        // Generate new placeholder
        const placeholder = `image_${updatedImages.length + 1}`;

        // Replace base64 string with placeholder in email content
        updatedContent = updatedContent.replace(imgSrc, placeholder);

        // Add new image to state without URL
        updatedImages.push({
          fileName: `image-${updatedImages.length + 1}`,
          placeholder,
          url: null,
          isUsed: true,
          base64String: imgSrc,
        });

        // Upload image and update state
        const uploadResponse = await uploadImage(file, imgSrc);
        if (uploadResponse !== null) {
          const { url, s3_key } = uploadResponse;
          updatedImages[updatedImages.length - 1].url = url;
          updatedImages[updatedImages.length - 1].s3_key = s3_key || null;
        }
      }
    }


    setEmailContent(updatedContent);
    setEmailImages(updatedImages);

    // Restore cursor position
    setTimeout(() => {
      if (range) {
        editor.setSelection(range.index, range.length);
      }
    }, 0);
  }, [quillInstance, emailImages]);

  interface IRestoreImageFromPlaceholder {
    (newImageUrl: string): void;
  }

  const uploadImage = async (file: File, imgSrc: string): Promise<IEmailImage | null> => {
    if (!file) {
      console.error("❌ No file selected.");
      return null;
    }

    const placeholder = `image_${emailImages.length + 1}`; // Generate placeholder here
    const formData = new FormData();
    formData.append("file", file);
    formData.append("campaignId", id || "");
    formData.append("placeholder", placeholder);

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_SERVER_URL}/api/v1/company/outreach/upload-image`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );

      const imageData = response.data;
      if (!imageData.url) {
        console.error("❌ Image URL missing:", response.data);
        return null;
      }


      // Replace base64 string with placeholder in email content
      setEmailContent((prevContent) =>
        prevContent.replace(imgSrc, imageData.url)
      );

      // Update emailImages state
      setEmailImages((prevImages) => [
        ...prevImages,
        {
          fileName: imageData.fileName,
          placeholder: imageData.placeholder, // Ensure it's set
          url: imageData.url,
          isUsed: true,
          base64String: "",
          s3Key: imageData.s3Key,
        },
      ]);

      return imageData;
    } catch (error) {
      console.error("❌ Error uploading image:", error);
      return null;
    }
  };

  useEffect(() => {
    if (quillRef.current && !quillInstance) {
      const quill = quillRef.current.getEditor();
      if (quill) {

        setQuillInstance(quill);
      }
    }
  }, [quillRef, quillInstance]);

  useEffect(() => {

  }, [emailImages]);

  // Force re-render when content updates
  useEffect(() => {
    setQuillKey((prevKey) => prevKey + 1);
  }, [emailContent]);

  const modules = {
    toolbar: {
      container: [
        [{ header: [1, 2, false] }],
        ["bold", "italic", "underline"],
        [{ list: "ordered" }, { list: "bullet" }],
        ["image"],
      ],
    },
    clipboard: { matchVisual: false },
  };

  const removeItem = (item: string | number, list: any[], setList: Function) => {
    setList(list.filter((i) => i !== item));
  };

  return (
    <div className="p-6 bg-gray-100 min-h-screen">
      <h1 className="text-3xl font-bold mb-6 text-gray-800">
        {isEditing ? "Edit Campaign" : "New Campaign"}
      </h1>

      <div className="bg-white shadow rounded-lg p-6">
        <InputField
          label="Campaign Name"
          value={campaignName}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setCampaignName(e.target.value)
          }
          onBlur={() => setTouched((prev) => ({ ...prev, name: true }))}
          error={errors.name}
          touched={touched.name}
        />

        {/* Email Subject */}
        <InputField
          label="Subject"
          value={subject}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setSubject(e.target.value)
          }
          onBlur={() => setTouched((prev) => ({ ...prev, subject: true }))}
          error={errors.subject}
          touched={touched.subject}
        />

        {/* Date/Time Picker for Sending Campaign */}
        <div className="mt-4 mb-4">
          <label className="block text-gray-700 font-semibold mb-2">
            Send Date/Time
          </label>
          <input
            type="datetime-local"
            className="w-full p-2 border border-gray-300 rounded-lg focus:ring focus:ring-blue-300"
            value={sendDate}
            onChange={(e) => setSendDate(e.target.value)}
          />
        </div>

        {/* Recipients Section */}
        <label className="block text-gray-700 font-semibold mb-2">
          Recipients
        </label>

        <div className="flex flex-row justify-start items-center">
          <button
            className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition mr-2"
            onClick={() => setIsFilterModalOpen(true)}
          >
            Filter Recipients
          </button>

          {/* Include ZIP Codes */}
          {includeZipCodes.map((zip) => (
            <span key={zip} className="flex items-center bg-blue-200 px-2 py-1 rounded-lg mr-2">
              <button className="mr-2 text-red-500 font-bold" onClick={() => removeItem(zip, includeZipCodes, setIncludeZipCodes)}>
                ❌
              </button>
              {zip} (Include)
            </span>
          ))}

          {/* Exclude ZIP Codes */}
          {excludeZipCodes.map((zip) => (
            <span key={zip} className="flex items-center bg-red-200 px-2 py-1 rounded-lg mr-2">
              <button className="mr-2 text-red-500 font-bold" onClick={() => removeItem(zip, excludeZipCodes, setExcludeZipCodes)}>
                ❌
              </button>
              {zip} (Exclude)
            </span>
          ))}

          {/* Include Cities */}
          {includeCities.map((city) => (
            <span key={city} className="flex items-center bg-blue-200 px-2 py-1 rounded-lg mr-2">
              <button className="mr-2 text-red-500 font-bold" onClick={() => removeItem(city, includeCities, setIncludeCities)}>
                ❌
              </button>
              {city} (Include)
            </span>
          ))}

          {/* Exclude Cities */}
          {excludeCities.map((city) => (
            <span key={city} className="flex items-center bg-red-200 px-2 py-1 rounded-lg mr-2">
              <button className="mr-2 text-red-500 font-bold" onClick={() => removeItem(city, excludeCities, setExcludeCities)}>
                ❌
              </button>
              {city} (Exclude)
            </span>
          ))}

          {/* Include Tags */}
          {includeTags.map((tagId) => {
            const tag = tags.find((t) => Number(t.id) === tagId);
            return tag && (
              <span key={tagId} className="flex items-center bg-blue-200 px-2 py-1 rounded-lg mr-2">
                <button className="mr-2 text-red-500 font-bold" onClick={() => removeItem(tagId, includeTags, setIncludeTags)}>
                  ❌
                </button>
                {tag.label} (Include)
              </span>
            );
          })}

          {/* Exclude Tags */}
          {excludeTags.map((tagId) => {
            const tag = tags.find((t) => Number(t.id) === tagId);
            return tag && (
              <span key={tagId} className="flex items-center bg-red-200 px-2 py-1 rounded-lg mr-2">
                <button className="mr-2 text-red-500 font-bold" onClick={() => removeItem(tagId, excludeTags, setExcludeTags)}>
                  ❌
                </button>
                {tag.label} (Exclude)
              </span>
            );
          })}
        </div>

        <label className="mt-2 block text-gray-700 font-semibold mb-2">
          Email Content
        </label>
        <div
          className={`border rounded-lg ${errors.emailContent && touched.emailContent
            ? "border-red-500"
            : "border-gray-100"
            } overflow-hidden`}
        >
          <style>
            {`
              .ql-toolbar.ql-snow {
                border-top-left-radius: 0.5rem;
                border-top-right-radius: 0.5rem;
              }
              .ql-container.ql-snow {
                border-top-left-radius: 0;
                border-top-right-radius: 0;
                border-bottom-left-radius: 0.5rem;
                border-bottom-right-radius: 0.5rem;
              }
            `}
          </style>
          <div className="flex">
            <div className="flex-1">
              <ReactQuill
                ref={quillRef}
                value={emailContent}
                onChange={handleEmailContentChange}
                theme="snow"
                modules={modules}
              />
            </div>

            {/* Unused Images Panel */}
            {emailImages.some((img) => !img.isUsed) && (
              <div className="w-60 h-full overflow-y-auto bg-gray-100 p-3 border rounded-lg shadow-md">
                <h3 className="text-sm font-bold text-gray-800 mb-2">Unused Images</h3>

                {emailImages
                  .filter((img) => !img.isUsed)
                  .map((img, index) => (
                    <div key={index} className="relative mb-3 bg-white p-2 rounded-lg shadow">
                      <img src={img.url || img.placeholder || ""} alt="Unused" className="w-full h-auto rounded-lg" />

                      <button
                        className="absolute top-1 left-1 bg-blue-500 text-white text-xs p-1 rounded-full hover:bg-blue-600"
                        onClick={() => { }}
                      >
                        ⬅️ Restore
                      </button>

                      <button
                        className="absolute top-1 right-1 bg-red-500 text-white text-xs p-1 rounded-full hover:bg-red-600"
                        onClick={() => setEmailImages((prev) => prev.filter((image) => image.url !== img.url))}
                      >
                        ❌ Delete
                      </button>
                    </div>
                  ))}

                {/* Show Placeholder Entries */}
                {Array.from({ length: 5 }).map((_, i) => (
                  <div key={`placeholder_${i}`} className="relative mb-3 bg-gray-200 p-2 rounded-lg shadow text-gray-500 text-sm">
                    {`{image_${i + 1}}`} (Placeholder)
                  </div>
                ))}
              </div>
            )}

          </div>


        </div>
        {
          errors.emailContent && touched.emailContent && (
            <p className="text-red-500 text-sm mt-1">{errors.emailContent}</p>
          )
        }

        {/* Upload Image Button */}
        <div className="flex justify-end gap-4 mt-4">
          <button
            className={`px-4 py-2 text-white rounded-lg transition ${isFormValid && !loading
              ? "bg-green-500 hover:bg-green-600"
              : "bg-gray-400 cursor-not-allowed"
              }`}
            onClick={() => handleSubmit(emailImages)}
            disabled={!isFormValid || loading}
          >
            {loading ? "Saving..." : "Save"}
          </button>
        </div>

        {/* Filter Modal */}
        <FilterRecipientsModal
          isOpen={isFilterModalOpen}
          onClose={() => setIsFilterModalOpen(false)}
          tags={tags}
          includeZipCodes={includeZipCodes}
          setIncludeZipCodes={setIncludeZipCodes}
          excludeZipCodes={excludeZipCodes}
          setExcludeZipCodes={setExcludeZipCodes}
          includeCities={includeCities}
          setIncludeCities={setIncludeCities}
          excludeCities={excludeCities}
          setExcludeCities={setExcludeCities}
          includeStates={includeStates}
          setIncludeStates={setIncludeStates}
          excludeStates={excludeStates}
          setExcludeStates={setExcludeStates}
          includeTags={includeTags}
          setIncludeTags={setIncludeTags}
          excludeTags={excludeTags}
          setExcludeTags={setExcludeTags}
        />
      </div >
    </div >
  );
};

export default NewCampaign;
